/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface BaseMedicalControlContactStateProps {
  id: string;
  stateAbbrevation: string;
  baseMedicalControlContactId: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class BaseMedicalControlContactStateRecord extends Record<BaseMedicalControlContactStateProps>(
  {
    id: null as any,
    stateAbbrevation: null as any,
    baseMedicalControlContactId: null as any,
    companyId: null as any,
    dateLastUpdated: null as any,
    dateCreated: null as any,
    dateDeleted: null as any,
    lastUpdatedById: null as any,
  },
) {
  public static fromJS(json: {
    id: string;
    stateAbbrevation: string;
    baseMedicalControlContactId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): BaseMedicalControlContactStateRecord {
    if (json == null) {
      throw new Error('BaseMedicalControlContactStateRecord.fromJS: parameter cannot be null');
    }
    return new BaseMedicalControlContactStateRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactStateJson.id',
      ),
      stateAbbrevation: getFromOrThrow(
        json,
        (j) => j.stateAbbrevation,
        'BaseMedicalControlContactStateJson.stateAbbrevation',
      ),
      baseMedicalControlContactId: json.baseMedicalControlContactId,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'BaseMedicalControlContactStateJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    stateAbbrevation: string;
    baseMedicalControlContactId: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [BaseMedicalControlContactStateRecord, Error[]] {
    if (json == null) {
      throw new Error(
        'BaseMedicalControlContactStateRecord.fromJSUnsafe: parameter cannot be null',
      );
    }
    const errors: Error[] = [];

    const record = new BaseMedicalControlContactStateRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactStateJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      stateAbbrevation: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => j.stateAbbrevation,
            'BaseMedicalControlContactStateJson.stateAbbrevation',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      baseMedicalControlContactId: tryOrDefault(
        () => json.baseMedicalControlContactId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'BaseMedicalControlContactStateJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: BaseMedicalControlContactStateProps) {
    super(props);
    if (props == null) {
      throw new Error('BaseMedicalControlContactStateRecord constructor parameter cannot be null');
    }
  }
}

export abstract class BaseMedicalControlContactStateViewModelBase
  implements BaseMedicalControlContactStateProps
{
  protected readonly _props: BaseMedicalControlContactStateProps;
  private _json?: BaseMedicalControlContactStateProps;

  get id(): string {
    return this._props.id;
  }
  get stateAbbrevation(): string {
    return this._props.stateAbbrevation;
  }
  get baseMedicalControlContactId(): string | null {
    return this._props.baseMedicalControlContactId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: BaseMedicalControlContactStateProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        stateAbbrevation: this._props.stateAbbrevation,
        baseMedicalControlContactId: this._props.baseMedicalControlContactId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllBaseMedicalControlContactStateEntities = (state: AppState) =>
  state.entityStores.baseMedicalControlContactState.entities;

export const selectBaseMedicalControlContactStateById = (id: string) => (state: AppState) =>
  state.entityStores.baseMedicalControlContactState.entities.get(id);

export const selectBaseMedicalControlContactStateMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.baseMedicalControlContactState.meta.get(id);

export const selectBaseMedicalControlContactStateAndMetadataById = (id: string) =>
  createSelector(
    selectBaseMedicalControlContactStateById(id),
    selectBaseMedicalControlContactStateMetadataById(id),
    (baseMedicalControlContactState, meta) => ({ baseMedicalControlContactState, meta }),
  );

export function selectBaseMedicalControlContactStateEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllBaseMedicalControlContactStateEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, BaseMedicalControlContactStateRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectBaseMedicalControlContactStateEntitiesByStateAbbrevation(
  stateAbbrevation: string,
) {
  return createSelector(selectAllBaseMedicalControlContactStateEntities, (entities) =>
    entities.filter((e) => e.stateAbbrevation === stateAbbrevation).valueSeq(),
  );
}

export function selectBaseMedicalControlContactStateEntitiesByBaseMedicalControlContactId(
  baseMedicalControlContactId: string,
) {
  return createSelector(selectAllBaseMedicalControlContactStateEntities, (entities) =>
    entities
      .filter((e) => e.baseMedicalControlContactId === baseMedicalControlContactId)
      .valueSeq(),
  );
}

export function selectBaseMedicalControlContactStateEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllBaseMedicalControlContactStateEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
